import React, { useState } from "react";
import SkeyepredictUsersTable from "./SkeyepredictUsersTable";
import {
  AddUserDialog,
  EditUserDialog,
  EditRoleDialog,
  ManageAccessDialog,
} from "./SkeyepredictUsersDialogs";
import {
  fetchUsers,
  createUser,
  updateUser,
  deleteUser,
  updateUserRole,
  updateUserAccess,
} from "./SkeyepredictUsersAPI";
import PageLayout from "../../../components/PageLayout";
import SearchToolbar from "../../../components/SearchToolbar";
import usePageTitle from "../../../services/usePageTitle";
import usePageState from "../../../hooks/usePageState";
import useIsRoleUser from "../../../hooks/useIsRoleUser";
import skeyepredictUsersHelp from "../../../content/help/skeyepredict-users.json";

export default function SkeyepredictUsers({
  pageTitle = "SkeyePredict Users",
}) {
  usePageTitle("SkeyePredict Users - SkeyeGate™");

  const {
    filteredData,
    loading,
    searchText,
    page,
    pageSize,
    messageApi,
    contextHolder,
    handleSearch,
    clearFilter,
    loadData,
    onPageChange,
  } = usePageState({
    fetchFn: fetchUsers,
    filterFields: ["username", "email"],
  });

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [accessModalOpen, setAccessModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const isRoleUser = useIsRoleUser();

  const handleAddUser = async (values) => {
    await createUser(values);
    messageApi.success("User created successfully");
    loadData();
  };

  const handleEditUser = async (userId, values) => {
    await updateUser(userId, values);
    messageApi.success("User updated successfully");
    loadData();
  };

  const handleEditRole = async (userId, role) => {
    await updateUserRole(userId, role);
    messageApi.success("User role updated successfully");
    loadData();
  };

  const handleManageAccess = async (userId, values) => {
    await updateUserAccess(userId, values);
    messageApi.success("User access managed successfully");
    loadData();
  };

  const handleDeleteUser = async (userId) => {
    await deleteUser(userId);
    messageApi.success("User deleted successfully");
    loadData();
  };

  return (
    <PageLayout
      title={pageTitle}
      logo="/img/majesteye-icon-skeye-predict-square-white-background-no-label"
      description="Manager for SkeyePredict users, allowing you to create, edit, and organize your user accounts and permissions in one place."
      helpContent={skeyepredictUsersHelp}
    >
      {contextHolder}
      <SearchToolbar
        search={searchText}
        onSearchChange={handleSearch}
        onClearFilter={clearFilter}
        addButtonLabel="Add User"
        onAdd={() => setAddModalOpen(true)}
        showAdd={!isRoleUser}
      />
      <SkeyepredictUsersTable
        data={filteredData}
        loading={loading}
        page={page}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onEditUser={(user) => {
          setSelectedUser(user);
          setEditModalOpen(true);
        }}
        onEditRole={(user) => {
          setSelectedUser(user);
          setRoleModalOpen(true);
        }}
        onManageAccess={(user) => {
          setSelectedUser(user);
          setAccessModalOpen(true);
        }}
        onDeleteUser={handleDeleteUser}
        isRoleUser={isRoleUser}
      />
      <AddUserDialog
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSave={handleAddUser}
      />
      <EditUserDialog
        open={editModalOpen}
        user={selectedUser}
        onClose={() => setEditModalOpen(false)}
        onSave={handleEditUser}
      />
      <EditRoleDialog
        open={roleModalOpen}
        user={selectedUser}
        onClose={() => setRoleModalOpen(false)}
        onSave={handleEditRole}
      />
      <ManageAccessDialog
        open={accessModalOpen}
        user={selectedUser}
        onClose={() => setAccessModalOpen(false)}
        onSave={handleManageAccess}
      />
    </PageLayout>
  );
}
